import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { ensureRouteData } from '@/app/query-client'
import { brokerDetailQueryOptions } from '@/features/brokers/api/queries'
import { defaultBroker } from '@/features/brokers/lib/defaultTargets'
import { readLastBroker } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { DEFAULT_PERIOD } from '@/screens/brokers/search'

/**
 * The `/brokers/by-broker` loader — the sidebar's "Broker's Choice" row with
 * no house in view.
 *
 * `by-broker`, not `choice` or `broker`: it matches the row's own internal
 * key in `nav-groups.ts` (`brokersGroup`'s `current` already reads `'by-
 * broker'` for the real `/brokers/$code` page), and it reads as a sibling of
 * `by-stock` rather than a second, differently-shaped name for the same idea.
 *
 * VALIDATES last-used before trusting it — `brokerDetailQueryOptions` now
 * answers a bad code with `null` rather than throwing (docs/route-loading.md,
 * "Not-found answers"), which is exactly the signal this loader needs to
 * fall back to `busiest_code` off Overview instead of redirecting to a page
 * that only says "no broker with this number". See `ByStockRedirect.loader.ts`
 * for the "always throws", "one fallback, never a loop" and
 * hover-preload-does-not-navigate notes; all apply here unchanged.
 *
 * `validateLastBroker` also swallows a genuine READ failure (a 5xx, a device
 * that drops offline mid-check) into "not valid", the same as a confirmed bad
 * code — see that function's own comment for why: a broken CHECK is not a
 * broken redirect, and only the DEFAULT lookup below is allowed to surface as
 * this route's own error page.
 */
async function validateLastBroker(client: QueryClient, code: string): Promise<boolean> {
  try {
    return (await ensureRouteData(client, brokerDetailQueryOptions(code, DEFAULT_PERIOD))) !== null
  } catch {
    return false
  }
}

/**
 * The house a param-less broker row opens: the member's last-used one while
 * it still exists, else the market's busiest — its detail read either way.
 * Shared with `/brokers/reviews` (`ReviewsRedirect.loader.ts`), which opens
 * the same house on its reviews page.
 */
export async function resolveBrokerCode(queryClient: QueryClient): Promise<string> {
  const lastUsed = readLastBroker()
  if (lastUsed !== null && (await validateLastBroker(queryClient, lastUsed))) return lastUsed

  const code = await defaultBroker(queryClient)
  await ensureRouteData(queryClient, brokerDetailQueryOptions(code, DEFAULT_PERIOD))
  return code
}

export async function loadByBrokerRedirect({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<never> {
  const code = await resolveBrokerCode(queryClient)
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/brokers/$code', params: { code }, replace: true })
}
